// app/static/js/react/src/components/quality/QualityAssessmentModal.js
import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import useAuth from "../../hooks/useAuth";

const QualityAssessmentModal = ({ isOpen, onClose, sound_id }) => {
  if (!isOpen || !sound_id) return null;
  const { user, canManageUsers } = useAuth();
  const [soundData, setSoundData] = useState(null);
  const [soundTextData, setSoundTextData] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [textLoading, setTextLoading] = useState(true);
  const [viewMode, setViewMode] = useState('monolog'); // 'monolog' or 'dialog'
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState('');
  // Локальное состояние для выбранных опций критериев
  const [localAssessmentItems, setLocalAssessmentItems] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Загрузка данных при открытии модального окна
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Получаем данные звонка и оценки
        const soundResponse = await api.get(`/sound/${sound_id}`);
        const data = soundResponse.data;
        setSoundData(data);
        
        // Инициализация локального состояния выбранных критериев
        const initialItems = {};
        if (data.assessment?.items) {
          data.assessment.items.forEach(item => {
            initialItems[item.criteria_id] = item.option_id;
          });
        }
        setLocalAssessmentItems(initialItems);

        // Получаем критерии и ответы для проекта звонка
        if (data.assessment?.project_id) {
          const criteriaResponse = await api.get(`/quality/criteria?project_id=${data.assessment.project_id}`);
          const rawCriteria = criteriaResponse.data.data || [];
          // Уникализация по id на случай дублей
          const uniqueCriteria = Array.from(new Map(rawCriteria.map(c => [c.id, c])).values());
          setCriteria(uniqueCriteria);
        }
        
        const resTextResponse = await api.get(`/sound/${sound_id}/text`);
        setSoundTextData(resTextResponse.data);
        if (resTextResponse.data.text?.dialog) {
          setViewMode('dialog');
        }
        
        // Загружаем аудио
        if (data.has_audio) {
          setAudioLoading(true);
          try {
            const res = await api.get(`/sound/audio/${sound_id}`, {
              responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            setAudioUrl(url);
          } catch (err) {
            console.error('Ошибка загрузки аудио:', err);
          } finally {
            setAudioLoading(false);
            setTextLoading(false);
          }
        }
      } catch (err) {
        setError(err.message || 'Ошибка при загрузке данных');
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [sound_id]);
  console.log('soundData: ', soundData);
  console.log('soundTextData: ', soundTextData);

  // --- Функция для разметки строк с цветами ---
  const renderText = () => {
    if (viewMode === 'dialog' && soundTextData?.text?.dialog) {
      const lines = soundTextData.text.dialog.split('\n').filter(line => line.trim() !== '');
      return lines.map((line, i) => {
        let role = null;
        let timeText = '';
        let mainText = '';

        if (line.startsWith('Речь оператора')) {
          role = 'operator';
        } else if (line.startsWith('Речь клиента')) {
          role = 'client';
        }

        if (role) {
          const parts = line.split(']: ');
          if (parts.length >= 2) {
            timeText = parts[0] + ']:';
            mainText = parts.slice(1).join(']: ');
          } else {
            timeText = line;
          }
        }

        return (
          <div key={i} className={`dialog-line ${role}`}>
            <span className="time">{timeText.split(']:')[0]}]:</span>
            <span className="text">{mainText}</span>
          </div>
        );
      });
    }

    return <pre className="text-modal-text">{soundTextData?.text?.monolog || ''}</pre>;
  };

  const [activeTab, setActiveTab] = useState('criteria');
  const [isInternChecked, setIsInternChecked] = useState(false);

  // Загрузка значения intern при открытии модального окна
  useEffect(() => {
    if (soundData?.assessment?.intern !== undefined) {
      setIsInternChecked(soundData.assessment.intern);
    }
  }, [soundData?.assessment?.intern]);

  // Очистка URL при размонтировании
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  if (isLoading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Оценка качества звонка</h3>
            <button className="btn-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="loading-container">Загрузка данных...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Оценка качества звонка</h3>
            <button className="btn-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="error-container">Ошибка: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Функция сохранения оценки
  const handleSaveAssessment = async () => {
    if (!soundData?.assessment?.id) return;

    setIsSaving(true);
    try {
      // Подготавливаем items из localAssessmentItems
      const items = Object.entries(localAssessmentItems)
        .filter(([_, optionId]) => optionId !== null && optionId !== '')
        .map(([criteria_id, option_id]) => ({
          criteria_id: parseInt(criteria_id),
          option_id: parseInt(option_id)
        }));

      const response = await api.put(`/quality/assessment/${soundData.assessment.id}`, {
        items
      });

      // Обновляем soundData.assessment.items
      setSoundData(prev => ({
        ...prev,
        assessment: {
          ...prev.assessment,
          items: response.data.items // Предполагаем, что сервер возвращает обновлённые items
        }
      }));

      alert('Оценка успешно сохранена');
    } catch (err) {
      console.error('Ошибка сохранения оценки:', err);
      alert('Ошибка при сохранении оценки');
    } finally {
      setIsSaving(false);
    }
  };

  // Функция обновления поля intern (дополнительный звонок)
  const handleToggleIntern = async () => {
    if (!soundData?.assessment?.id) return;

    try {
      const response = await api.patch(`/quality/assessment/update-intern/${soundData.assessment.id}`);
      
      // Обновляем soundData.assessment.intern
      setSoundData(prev => ({
        ...prev,
        assessment: {
          ...prev.assessment,
          intern: response.data.data.intern
        }
      }));
      
      // Обновляем локальное состояние чекбокса
      setIsInternChecked(response.data.data.intern);
    } catch (err) {
      console.error('Ошибка обновления поля intern:', err);
      alert('Ошибка при обновлении статуса "Дополнительный звонок"');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Оценка качества звонка</h3>
          <button className="btn-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {/* Информационный хедер с данными звонка */}
          <div className="sound-info-header">
            <div className="sound-info-item">
              <span className="sound-info-label">Оператор</span>
              <span className="sound-info-value">{soundData.name_operator}</span>
            </div>
            <div className="sound-info-item">
              <span className="sound-info-label">Дата звонка</span>
              <span className="sound-info-value">{soundData.date}</span>
            </div>
            <div className="sound-info-item">
              <span className="sound-info-label">Телефон</span>
              <span className="sound-info-value">{soundData.phone}</span>
            </div>
            <div className="sound-info-item">
              <span className="sound-info-label">Длительность</span>
              <span className="sound-info-value">{soundData.time_sound}</span>
            </div>
            <div className="sound-info-item">
              <span className="sound-info-label">Сторона сброса</span>
              <span className="sound-info-value">{soundData.stopside === 'a' ? 'Клиент' : soundData.stopside === 'b' ? 'Оператор' : soundData.stopside}</span>
            </div>
          </div>

          {/* Аудиоплеер */}
          {soundData && (
            <div className="text-modal-player px-3 pt-2 pb-3">
              {audioLoading ? (
                <div className="text-center text-muted">
                  <small>Загрузка аудио...</small>
                </div>
              ) : audioUrl ? (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  className="w-100"
                  autoPlay={false}
                  controls
                />
              ) : soundData.has_audio ? (
                <div className="text-center text-danger">
                  <small>❌ Не удалось загрузить аудио</small>
                </div>
              ) : (
                <div className="text-center text-muted">
                  <small>Аудиофайл недоступен</small>
                </div>
              )}
            </div>
          )}

          {/* Вкладки */}
          <div className="tabs-container">
            <button
              className={activeTab === 'criteria' ? 'tab-active' : 'tab'}
              onClick={() => setActiveTab('criteria')}
            >
              Критерии
            </button>
            <button
              className={activeTab === 'text' ? 'tab-active' : 'tab'}
              onClick={() => setActiveTab('text')}
            >
              Текст
            </button>
          </div>

          {/* Контент вкладок */}
          <div className="tab-content">

            {/* Кнопка сохранения изменений и чекбокс "Дополнительный звонок" */}
            {activeTab === 'criteria' && soundData?.assessment?.id && canManageUsers && (
              <div className="mt-3 d-flex justify-content-end align-items-center">
                {/* Чекбокс "Дополнительный звонок" */}
                <div className="form-check me-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="internCheckbox"
                    checked={isInternChecked}
                    onChange={handleToggleIntern}
                  />
                  <label className="form-check-label" htmlFor="internCheckbox">
                    Дополнительный звонок
                  </label>
                </div>
                {/* Кнопка сохранения */}
                <button
                  className="btn btn-primary"
                  onClick={handleSaveAssessment}
                  disabled={isSaving}
                >
                  {isSaving ? 'Сохранение...' : 'Сохранить изменения'}
                </button>
              </div>
            )}
            {/* Вкладка Критерии */}
            {activeTab === 'criteria' && (
              <div className="criteria-list">
                {/* Сначала выводим критерии, которые уже есть в оценке */}
                {soundData.assessment?.items
                  ?.filter(item => {
                    const criteriaItem = criteria.find(c => c.id === item.criteria_id);
                    return criteriaItem;
                  })
                  .map((item) => {
                    const criteriaItem = criteria.find(c => c.id === item.criteria_id);
                    if (!criteriaItem) return null;

                    const hasEditor = item.editor_name;
                    const currentOption = criteriaItem.options.find(o => o.id === item.option_id);
                    const currentOptionText = currentOption ? `${currentOption.text} (баллы: ${currentOption.score})` : '';

                    return (
                      <div
                        key={item.criteria_id}
                        className="criteria-item"
                        style={{ 
                          backgroundColor: item.creator_name === "System" ? '#FFC10720' : '#17A2B820' // Желтый для System, синий для пользователя
                        }}
                      >
                        <div className="criteria-header">
                          <div className="criteria-label">
                            <strong>{criteriaItem.name}</strong>
                            {hasEditor ? (
                              <span className="badge bg-warning ms-2">Изменен пользователем</span>
                            ) : (
                              <span className="badge bg-secondary ms-2">System</span>
                            )}
                          </div>
                          {canManageUsers ? (
                            <select
                              className="criteria-select"
                              value={localAssessmentItems[criteriaItem.id] || ''}
                              onChange={(e) => {
                                const optionId = e.target.value ? parseInt(e.target.value) : null;
                                setLocalAssessmentItems(prev => ({
                                  ...prev,
                                  [criteriaItem.id]: optionId
                                }));
                              }}
                            >
                              <option value="">Выберите вариант</option>
                              {criteriaItem.options.map(option => (
                                <option key={option.id} value={option.id}>
                                  {option.text} (баллы: {option.score})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="criteria-value">
                              {currentOptionText || 'Не выбрано'}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                }

                {/* Разделитель */}
                <div className="divider my-3" style={{ borderTop: '1px solid #ddd' }}></div>

                {/* Критерии, которые еще не добавлены в оценку - показываем только пользователям с canManageUsers */}
                {canManageUsers && criteria
                  .filter(c => !soundData.assessment?.items?.some(item => item.criteria_id === c.id))
                  .map((criteriaItem) => (
                    <div key={criteriaItem.id} className="criteria-item">
                      <div className="criteria-header">
                        <div className="criteria-label">
                          <strong>{criteriaItem.name}</strong>
                        </div>
                        <select
                          className="criteria-select"
                          value={localAssessmentItems[criteriaItem.id] || ''}
                          onChange={(e) => {
                            const optionId = e.target.value ? parseInt(e.target.value) : null;
                            setLocalAssessmentItems(prev => ({
                              ...prev,
                              [criteriaItem.id]: optionId
                            }));
                          }}
                        >
                          <option value="">Выберите вариант</option>
                          {criteriaItem.options.map(option => (
                            <option key={option.id} value={option.id}>
                              {option.text} (баллы: {option.score})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}

            {/* Вкладка Текст */}
            {activeTab === 'text' && (
              <div className="audio-text-tab">
                {/* Вкладки режимов текста */}
                <div className="text-modal-tabs">
                  <button
                    className={viewMode === 'monolog' ? 'active' : ''}
                    onClick={() => setViewMode('monolog')}
                  >
                    По времени
                  </button>
                  {soundTextData?.text?.dialog && (
                    <button
                      className={viewMode === 'dialog' ? 'active' : ''}
                      onClick={() => setViewMode('dialog')}
                    >
                      Диалог
                    </button>
                  )}
                </div>

                {/* Тело текста */}
                <div className="text-modal-body">
                  {textLoading ? (
                    <div className="text-center text-muted">Загрузка текста...</div>
                  ) : (
                    renderText()
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QualityAssessmentModal;